// useDataResidency.js
// Where this deployment keeps its patient records, for the app bar badge.
//
// TWO QUESTIONS, TWO SOURCES, and keeping them apart is the point:
//
//   Is this the on-premise build?   Build-time. `LOCAL_MODE` is baked in by
//                                   webpack's DefinePlugin, so the answer is
//                                   known before the first paint and cannot be
//                                   wrong. An on-premise install keeps records
//                                   in IndexedDB on the pharmacist's own
//                                   machine (see src/local/), which is a fact
//                                   about the bundle, not about a server.
//
//   Which region, if it is cloud?   The backend. `GET /api/deployment` answers
//                                   for the database it actually writes to (see
//                                   retroact/backend/config/residency.js). The
//                                   SPA must not hardcode a location: a
//                                   hardcoded residency claim is precisely the
//                                   kind that stays on screen for a year after
//                                   it stopped being true.
//
// FAILS SILENT, NOT WRONG. Any failure — the request, an unrecognised region, a
// backend too old to know the route — leaves `region` null and the badge renders
// nothing. A missing residency statement is recoverable; a false one is not.

import { ref } from 'vue';
import http from '../http-common';
import { LOCAL_MODE } from '@local/config';
import { DEMO_MODE } from '@demo/config';

/**
 * The region this deployment writes to, once known.
 * @type {import('vue').Ref<{id: string, label: string, labelKey: string, country: string}|null>}
 */
const region = ref(null);

let loadPromise = null;

async function fetchDeployment() {
  try {
    const { data } = await http.get('/deployment');
    // Shape-checked rather than trusted: `labelKey` is fed to $t() and `label`
    // is the fallback rendered when no translation exists, so a half-formed
    // payload would put a raw key in the app bar.
    const value = data?.region;
    region.value = value && value.id && value.labelKey && value.label ? value : null;
  } catch {
    region.value = null;
  }
}

/**
 * @returns {{
 *   isOnPremise: boolean,
 *   region: import('vue').Ref<object|null>,
 *   ensureLoaded: () => Promise<void>
 * }}
 */
export function useDataResidency() {
  // Neither of the replaced-data-layer builds may ask.
  //
  // On-premise: `http` is wired to the IndexedDB adapter, so the request would
  // be answered by the local store, which has no opinion about regions — and
  // the build already knows its own answer anyway.
  //
  // Demo: `http` is wired to the in-memory clinic. Its patients are invented,
  // so there is no residency to state and a badge would be decoration on a
  // marketing page.
  if (!LOCAL_MODE && !DEMO_MODE && !loadPromise) {
    loadPromise = fetchDeployment();
  }

  return {
    isOnPremise: LOCAL_MODE,
    region,
    ensureLoaded: () => loadPromise || Promise.resolve(),
  };
}
